import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { take } from 'rxjs/operators';
import {
  ExternalAuthService,
  ExternalAuthCallbackSuccess,
  ExternalAuthCallbackError
} from '../external-auth.service';
import { LoginService } from '../../login/login.service';
import { LoaderService } from '../../loader/loader.service';

@Component({
  selector: 'app-sso-callback',
  templateUrl: './sso-callback.component.html',
  styleUrls: ['./sso-callback.component.css']
})
export class SsoCallbackComponent implements OnInit {
  isProcessing = true;
  errorMessage: string | null = null;
  provider?: string;
  private missingSessionReason: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private externalAuthService: ExternalAuthService,
    private loginService: LoginService,
    private loaderService: LoaderService,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe(params => {
      this.handleCallback(params);
    });
  }

  retry(): void {
    this.router.navigate(['/login']);
  }

  private handleCallback(paramMap: ParamMap): void {
    const result = this.externalAuthService.parseCallback(paramMap);
    this.provider = result.provider;

    if (result.status === 'success') {
      this.handleSuccess(result);
    } else {
      this.handleError(result);
    }
  }

  private handleSuccess(result: ExternalAuthCallbackSuccess): void {
    const session = this.externalAuthService.buildSessionFromCallback(result);
    if (!session) {
      this.missingSessionReason = 'Received success status but required session fields were missing.';
      console.error('SSO callback: required session fields were missing.');
    } else {
      this.missingSessionReason = null;
    }

    if (!session) {
      this.handleMissingSession();
      return;
    }

    try {
      this.loginService.persistAuthSession(session);
    } catch (error) {
      this.missingSessionReason = 'Failed to persist the session locally.';
      console.error('SSO callback: failed to persist session', error);
      this.handleMissingSession();
      return;
    }

    const navigation = this.loginService.determinePostLoginNavigation(session);
    if (!navigation || !navigation.commands) {
      this.missingSessionReason = 'Unable to determine the next page after sign-in.';
      console.error('SSO callback: could not determine navigation', navigation);
    }
    if (!navigation || !navigation.commands) {
      this.handleMissingSession();
      return;
    }

    if (navigation.showLoader) {
      this.loaderService.show();
    }

    this.isProcessing = false;
    this.router.navigate(navigation.commands, { replaceUrl: true });
  }

  private handleError(result: ExternalAuthCallbackError): void {
    const message = this.getErrorMessage(result);
    this.errorMessage = message;
    this.isProcessing = false;
    this.notifier.notify('error', message);
  }

  private handleMissingSession(): void {
    const baseMessage = 'We were unable to complete single sign-on. Please try again or use your password.';
    this.errorMessage = this.missingSessionReason
      ? `${baseMessage} (${this.missingSessionReason})`
      : baseMessage;
    this.isProcessing = false;
    this.notifier.notify('error', this.errorMessage);
  }

  private getErrorMessage(result: ExternalAuthCallbackError): string {
    if (result.message) {
      return this.stripCorrelationReference(result.message);
    }

    switch (result.code) {
      case 'DomainNotAllowed':
        return 'Your email domain is not allow-listed for single sign-on. Please contact your administrator.';
      case 'AccountInactive':
        return 'Your account is inactive. Please contact your administrator for assistance.';
      case 'AccountLocked':
        return 'Your account is locked. Please contact your administrator.';
      case 'PendingApproval':
        return 'Your account request is pending approval. A portal administrator will notify you once access is granted.';
      case 'EmailMissing':
        return 'The identity provider did not share your email address. Please contact support.';
      case 'ProviderError':
        return 'The identity provider returned an error. Please try again.';
      default:
        return 'Single sign-on failed. Please try again or contact your administrator if the issue persists.';
    }
  }

  private stripCorrelationReference(message: string): string {
    if (!message) {
      return message;
    }

    return message.replace(/\s*\(ref:\s*[^\)]+\)\s*$/i, '').trim();
  }
}
